/** Rate Plan detail page with metadata, rate grid, and customer assignments. */
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
  Alert,
  Breadcrumbs,
  Link,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  NavigateNext as BreadcrumbSeparator,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  Assignment as AssignIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useReactTable, getCoreRowModel, getPaginationRowModel } from "@tanstack/react-table";
import apiClient from "../../api/client";
import CSVImportButton from "../../components/rate-plans/CSVImportButton";
import CSVExportButton from "../../components/rate-plans/CSVExportButton";
import RatePlanTable from "../../components/rate-plans/RatePlanTable";
import RatePlanAssignDialog from "../../components/rate-plans/RatePlanAssignDialog";

/** API base URL. */
const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/** Rate Plan interface. */
export interface RatePlan {
  id: string;
  name: string;
  description: string | null;
  created_by: string;
  is_active: boolean;
  rate_count: number;
  created_at: string;
  updated_at: string;
}

/** Customer assignment interface. */
interface CustomerAssignment {
  id: string;
  name: string;
  account_type: string;
  assigned_at: string;
}

/** Rate Plan Detail component. */
export default function RatePlanDetail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const [ratePlan, setRatePlan] = useState<RatePlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedIsActive, setEditedIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignments, setAssignments] = useState<CustomerAssignment[]>([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  /** Fetch rate plan details. */
  const fetchRatePlan = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get(`${API_BASE_URL}/api/v1/rate-plans/${id}`);
      const plan = response.data;
      setRatePlan(plan);
      setEditedName(plan.name);
      setEditedDescription(plan.description || "");
      setEditedIsActive(plan.is_active);
      // Edit action from the list page deep-links with ?edit=1
      if (searchParams.get("edit") === "1") {
        setEditing(true);
        setTimeout(() => nameInputRef.current?.focus(), 100);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to load rate plan");
    } finally {
      setLoading(false);
    }
  }, [id]);

  /** Fetch customer assignments. */
  const fetchAssignments = useCallback(async () => {
    if (!id) return;

    setAssignmentsLoading(true);
    try {
      const response = await apiClient.get(`${API_BASE_URL}/api/v1/rate-plans/${id}/assignments`);
      setAssignments(response.data.assignments || []);
    } catch (err: any) {
      console.error("Failed to load assignments:", err);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [id]);

  /** Save rate plan changes. */
  const handleSave = async () => {
    if (!id) return;

    setSaving(true);
    setError(null);
    try {
      await apiClient.put(`${API_BASE_URL}/api/v1/rate-plans/${id}`, {
        name: editedName,
        description: editedDescription || null,
        is_active: editedIsActive,
      });
      setEditing(false);
      fetchRatePlan(); // Refresh to get updated data
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to save rate plan");
    } finally {
      setSaving(false);
    }
  };

  /** Delete rate plan. */
  const handleDelete = async () => {
    if (!id) return;

    try {
      await apiClient.delete(`${API_BASE_URL}/api/v1/rate-plans/${id}`);
      navigate("/rate-plans");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Failed to delete rate plan");
      setDeleteDialogOpen(false);
    }
  };

  /** Handle CSV import success. */
  const handleImportSuccess = () => {
    fetchRatePlan();
    setRefreshTrigger((prev) => prev + 1);
  };

  /** Handle assignment success. */
  const handleAssignmentSuccess = () => {
    fetchAssignments();
  };

  /** Load data on mount. */
  useEffect(() => {
    fetchRatePlan();
    fetchAssignments();
  }, [fetchRatePlan, fetchAssignments]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!ratePlan) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Rate plan not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs
        separator={<BreadcrumbSeparator fontSize="small" />}
        sx={{ mb: 3 }}
      >
        <Link
          underline="hover"
          color="inherit"
          href="/rate-plans"
          onClick={(e) => {
            e.preventDefault();
            navigate("/rate-plans");
          }}
        >
          Rate Plans
        </Link>
        <Typography color="text.primary">{ratePlan.name}</Typography>
      </Breadcrumbs>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Plan Metadata Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ flexGrow: 1 }}>
            {editing ? (
              <TextField
                inputRef={nameInputRef}
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                size="small"
                sx={{ width: "100%", maxWidth: 600 }}
              />
            ) : (
              ratePlan.name
            )}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            {editing ? (
              <>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setEditing(false);
                    setEditedName(ratePlan.name);
                    setEditedDescription(ratePlan.description || "");
                    setEditedIsActive(ratePlan.is_active);
                  }}
                  disabled={saving}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Tooltip title="Edit Plan">
                  <IconButton
                    size="small"
                    onClick={() => setEditing(true)}
                    color="primary"
                   aria-label="Edit">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Plan">
                  <IconButton
                    size="small"
                    onClick={() => setDeleteDialogOpen(true)}
                    color="error"
                   aria-label="Delete">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Description
            </Typography>
            {editing ? (
              <TextField
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                multiline
                rows={3}
                fullWidth
                size="small"
                placeholder="Add a description..."
              />
            ) : (
              <Typography variant="body1">
                {ratePlan.description || "No description"}
              </Typography>
            )}
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Plan Details
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Created By:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {ratePlan.created_by}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Created At:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {new Date(ratePlan.created_at).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="body2">Rate Count:</Typography>
                <Typography variant="body2" fontWeight="medium">
                  {ratePlan.rate_count}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography variant="body2">Status:</Typography>
                {editing ? (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editedIsActive}
                        onChange={(e) => setEditedIsActive(e.target.checked)}
                        size="small"
                      />
                    }
                    label={editedIsActive ? "Active" : "Inactive"}
                  />
                ) : (
                  <Chip
                    label={ratePlan.is_active ? "Active" : "Inactive"}
                    color={ratePlan.is_active ? "success" : "default"}
                    size="small"
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Rate Management Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Rates ({ratePlan.rate_count})
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <CSVImportButton
              ratePlanId={ratePlan.id}
              ratePlanName={ratePlan.name}
              onImportSuccess={handleImportSuccess}
            />
            <CSVExportButton
              ratePlanId={ratePlan.id}
              ratePlanName={ratePlan.name}
            />
          </Box>
        </Box>

        {/* Rate Table */}
        <RatePlanTable
          ratePlanId={ratePlan.id}
          refreshTrigger={refreshTrigger}
        />
      </Paper>

      {/* Customer Assignments Section */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            Assigned Customers ({assignments.length})
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AssignIcon />}
            onClick={() => setAssignDialogOpen(true)}
          >
            Assign to Customer
          </Button>
        </Box>

        {assignmentsLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : assignments.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No customers assigned to this rate plan yet.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer Name</TableCell>
                  <TableCell>Account Type</TableCell>
                  <TableCell>Assigned At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {assignments.map((assignment) => (
                  <TableRow key={assignment.id}>
                    <TableCell>{assignment.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={assignment.account_type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(assignment.assigned_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Rate Plan</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete rate plan "{ratePlan.name}"?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            Warning: This will delete all rates and customer assignments for this plan.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Dialog */}
      <RatePlanAssignDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        ratePlanId={ratePlan.id}
        ratePlanName={ratePlan.name}
        onAssignmentSuccess={handleAssignmentSuccess}
      />
    </Box>
  );
}
